import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, NgZone } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { ToastrService, ToastPackage } from 'ngx-toastr';
import { BaseToastComponent } from '@app/shared/toasts/base-toast.component';
import { AlertType, SecurityAlert } from './models/security-alert';
import { ImageService } from '@app/shared/services/image.service';
import { BehaviorSubject, map } from 'rxjs';

@Component({
  selector: 'app-security-alert',
  templateUrl: './security-alert.component.html',
  styleUrls: ['./security-alert.component.scss'],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class SecurityAlertComponent extends BaseToastComponent {

  safeHtml$ = new BehaviorSubject<SafeHtml>('');
  AlertType = AlertType;

  private readonly progressSubject = new BehaviorSubject<number>(0);
  readonly progress$ = this.progressSubject.asObservable();

  @Input()
  set progress(value: number) {
    const next = Number.isFinite(value) ? value : 0;
    this.progressSubject.next(next);
  }
  private readonly alertSubject = new BehaviorSubject<SecurityAlert | null>(null);
  readonly alert$ = this.alertSubject.asObservable();
  readonly cachedImageUrl$ = this.alert$.pipe(
    map((alert) => alert?.photoUrl ? this.imageService.getAdaptiveImage(alert.photoUrl) : null)
  );
  readonly alertCount$ = this.alert$.pipe(
    map((alert) => alert?.count ?? 1)
  );
  readonly shouldShowCount$ = this.alertCount$.pipe(
    map((count) => count > 1)
  );

  @Input()
  set alert(newAlert: SecurityAlert) {
    const next = newAlert ? { ...newAlert } : null;
    this.alertSubject.next(next);
    const safeContent = next?.content ?? '';
    this.safeHtml$.next(this.sanitizer.bypassSecurityTrustHtml(safeContent));
  }

  get alert(): SecurityAlert {
    return this.alertSubject.value as SecurityAlert;
  }

  constructor(
    protected toastrService: ToastrService,
    public toastPackage: ToastPackage,
    protected sanitizer: DomSanitizer,
    public imageService: ImageService,
    cdr: ChangeDetectorRef,
    ngZone: NgZone
  ) {
    super(toastrService, toastPackage, cdr, ngZone);

    const parsedAlert = JSON.parse(this.getToastMessage());
    this.alert = parsedAlert;
    this.originalTimeOut = this.toastPackage.config.timeOut || 0;
  }

  dismiss() {
    this.toastrService.clear(this.toastPackage.toastId);
  }
}
